import { creditOptions } from '@custom/Utils/constants/appConstants';
import { Icon } from '@ohif/ui';
import React, { useState, useRef, useEffect } from 'react';
import './index.css'

const CreditTopUpModal = ({ isOpen, onClose, onSubmit }) => {
  const [credits, setCredits] = useState(0);
  const [error, setError] = useState('');
  const modalRef = useRef(null);

  const closeHandler = () => {
    onClose();
    setCredits(0);
    setError('');
  };

  const handleAddCredits = (amount) => {
    setCredits((prev) => prev + amount);
    setError('');
  };

  const handleSubmit = () => {
    if (credits < 100) {
      setError('Minimum credit top-up is 100.');
      return;
    } else if (credits > 100000) {
      setError('Maximum credit top-up is 100000.');
      return;
    }

    onSubmit(credits);
    setCredits(0);
    setError('');
    closeHandler();
  };

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeHandler();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '1rem',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
      }}
    >
      <div
        ref={modalRef}
        className="bg-white w-full max-w-lg rounded-2xl shadow-xl p-6 relative"
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-2xl text-center font-bold text-gray-900">
            Top-up Credits
          </h2>
          <button
            onClick={closeHandler}
            className="text-gray-600 hover:text-red-500 text-2xl"
          >
            &times;
          </button>
        </div>

        {/* Credit Display */}
        <div className="!border-2 !border-teal-600 rounded-lg p-4 flex items-center text-3xl font-bold text-teal-600 mb-4">
          {/* Left Icon */}
          <span className="inline-flex items-center mr-3">
            <Icon
              className="h-[40px] w-[40px] mr-2"
              name={'creditIcon'}
            />
            <span className="w-[2px] h-14 bg-teal-600"></span>
          </span>

          {/* Middle: Editable Credit amount */}
          <input
            type="number"
            value={credits}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              if (isNaN(val)) {
                setCredits(0);
              } else {
                setCredits(val);
              }
              setError('');
            }}
            className="flex-1 text-3xl font-bold text-teal-600 bg-transparent outline-none text-start border-none w-[90%] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />

          {/* Rightmost: Clear button */}
          {credits > 0 && (
            <button
              onClick={() => {
                setCredits(0);
                setError('');
              }}
              className="text-teal-600 hover:text-red-600 text-3xl leading-none ml-4"
              title="Clear credits"
            >
              &times;
            </button>
          )}
        </div>


        {/* Error Message */}
        {error && (
          <div className="text-red-600 text-sm mb-4">{error}</div>
        )}

        {/* Credit Buttons */}
        <div className="grid grid-cols-4 sm:grid-cols-4 gap-4 mb-6">
          {creditOptions.map((amount) => (
            <button
              key={amount}
              onClick={() => handleAddCredits(amount)}
              className="bg-teal-600 text-white py-2 rounded-2xl font-medium hover:bg-teal-700 transition text-base"
            >
              +{amount}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3">
          <button
            onClick={closeHandler}
            className="px-4 py-2 text-gray-600 hover:text-red-500 rounded-lg transition text-md !border-2 !border-black bg-white shadow"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-5 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition text-md"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreditTopUpModal;
